import React, { useContext, useMemo, useState } from 'react';

type GradebookContextType = {
    fetchStudent: any;
    fetchActivity: string;
    cachedStudentData?: any;
    cachedActivityData?: any;
    cachedAvgData?: any;
    activitiesToFetch?: any;
    currentActLimit?: number;
    focusOnElement?: boolean;
    resetData?: boolean;
    activityStartDate: any;
    activityEndDate: any;
    resetTable?: boolean;
};

type GradebookDispatchType = {
    refetchGradebookData: (st: string, act: string, focusOnElement?: boolean) => void;
    saveGradebookLimit: (limit: number) => void;
    setActivitiesToFetch: (act: string) => void;
    resetGradebookData: (flag: boolean) => void;
    resetGradebookTable: (flag: boolean) => void;
    cacheGradebookData: (
        cachedStudentData: any,
        cachedActivityData: any,
        cachedAvgData: any,
        cachedActOffset: number,
    ) => void;
    saveGradebookActivityDateRange: (startDate: any, endDate: any) => void;
};

const GradebookContext = React.createContext<GradebookContextType | null>(null);
const GradebookDispatchContext = React.createContext<GradebookDispatchType | null>(null);

export function GradebookContextProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<GradebookContextType>({
        fetchStudent: '',
        fetchActivity: '',
        cachedStudentData: [],
        cachedActivityData: [],
        cachedAvgData: [],
        activitiesToFetch: [],
        currentActLimit: 0,
        focusOnElement: false,
        resetData: false,
        activityStartDate: null,
        activityEndDate: null,
        resetTable: false,
    });

    const actions = useMemo(
        () => ({
            saveGradebookActivityDateRange(startDate: any, endDate: any) {
                setState((s) => {
                    return {
                        ...s,
                        activityStartDate: startDate,
                        activityEndDate: endDate,
                    };
                });
            },
            refetchGradebookData(st: string, act: string, focusOnElement?: boolean) {
                setState((s) => {
                    return {
                        ...s,
                        fetchStudent: st,
                        fetchActivity: act,
                        focusOnElement: focusOnElement,
                    };
                });
            },
            saveGradebookLimit(limit: number) {
                setState((s) => {
                    return {
                        ...s,
                        currentActLimit: s.currentActLimit + limit,
                    };
                });
            },
            setActivitiesToFetch(act: string) {
                setState((s) => {
                    const currentArr = s.activitiesToFetch;
                    // console.log('currentArr', currentArr);
                    // console.log('act', act);
                    let newArr = [];
                    if (currentArr.length === 0) newArr = [act];
                    else newArr = currentArr?.includes(act) ? currentArr : currentArr?.push(act);
                    // console.log('newArr', newArr);
                    return {
                        ...s,
                        activitiesToFetch: newArr,
                    };
                });
            },
            resetGradebookData(flag: boolean) {
                setState((s) => {
                    return {
                        ...s,
                        resetData: flag,
                    };
                });
            },
            resetGradebookTable(flag: boolean) {
                setState((s) => {
                    return {
                        ...s,
                        resetTable: flag,
                    };
                });
            },

            cacheGradebookData(
                cachedStudentData: any,
                cachedActivityData: any,
                cachedAvgData: any,
                cachedActOffset: number,
            ) {
                setState((s) => {
                    return {
                        ...s,
                        cachedStudentData: cachedStudentData,
                        cachedActivityData: cachedActivityData,
                        cachedAvgData: cachedAvgData,
                        cachedActOffset: cachedActOffset,
                    };
                });
            },
        }),
        [setState],
    );

    return (
        <GradebookContext.Provider value={state}>
            <GradebookDispatchContext.Provider value={actions}>
                {children}
            </GradebookDispatchContext.Provider>
        </GradebookContext.Provider>
    );
}

export function useGradebookState() {
    const context = useContext(GradebookContext);
    if (context === null) {
        throw new Error('Expected Context for <Gradebook /> to be Initialized');
    }

    return context;
}

export function useGradebookDispatch() {
    const context = useContext(GradebookDispatchContext);
    if (context === null) {
        throw new Error('Expected Dispatch Context for <Gradebook /> to be Initialized');
    }

    return context;
}
